import {useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'
import { useAppDispatch } from 'src/redux/storeHooks'
import { requestPasswordForgetAsync } from 'src/redux/actions/authActions'
import useAppToast from 'src/hooks/useAppToast'
import ReCAPTCHA from 'react-google-recaptcha'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<ReCAPTCHA | null>(null)
  const captchaTokenRef = useRef<string | null>(null)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [isRateLimited, setIsRateLimited] = useState(false)
  const {successToast, errorToast} = useAppToast()
  const dispatch=useAppDispatch()
  const shouldShowEmailInlineError = (message?: string) => {
    const normalizedMessage = (message || '').toLowerCase()
    return (
      normalizedMessage.includes('email is incorrect') ||
      normalizedMessage.includes('email not found') ||
      normalizedMessage.includes('user not found')
    )
  }

  const resetCaptcha = () => {
    captchaRef.current?.reset()
    captchaTokenRef.current = null
    setCaptchaToken(null)
  }

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      const currentCaptchaToken = captchaTokenRef.current
      if (!currentCaptchaToken) {
        errorToast('Please complete captcha verification.');
        return;
      }
      setLoading(true);
      setHasErrors(undefined);
      setIsRateLimited(false);
      dispatch(
        requestPasswordForgetAsync({
          email: values.email,
          'g-recaptcha-response': currentCaptchaToken,
        })
      )
        .then((response: any) => {
          console.log("response:", response);
          const statusCode =
            response?.payload?.status_code ??
            response?.payload?.status ??
            response?.payload?.data?.status_code;
          const message =
            response?.payload?.massage ??
            response?.payload?.message ??
            response?.payload?.data?.massage ??
            response?.payload?.data?.message;

          if (statusCode === 200) {
            formik.resetForm()
            successToast(message || 'Password reset link sent successfully.');
            setHasErrors(false)
            setIsRateLimited(false)
            return;
          }

          if (statusCode === 400) {
            const errorMessage = message || 'Invalid request. Please check your email and try again.'
            errorToast(errorMessage);
            setHasErrors(shouldShowEmailInlineError(errorMessage) ? true : undefined)
            setIsRateLimited(false)
            resetCaptcha()
            return;
          }

          if (statusCode === 429) {
            errorToast(message || 'Too many attempts. Please wait and try again later.');
            setHasErrors(undefined)
            setIsRateLimited(true)
            resetCaptcha()
            return;
          }

          errorToast("An unexpected error occurred. Please try again later.");
          setHasErrors(true)
          resetCaptcha()
        })
        .catch((error: any) => {
          console.log("error while dispatching forgetpassword api", error);
          const statusCode = error?.payload?.status ?? error?.status ?? error?.response?.status;
          const message =
            error?.payload?.data?.massage ??
            error?.payload?.data?.message ??
            error?.data?.massage ??
            error?.data?.message ??
            error?.message;

          if (statusCode === 400) {
            const errorMessage = message || 'Invalid request. Please check your email and try again.'
            errorToast(errorMessage);
            setHasErrors(shouldShowEmailInlineError(errorMessage) ? true : undefined)
            setIsRateLimited(false)
            resetCaptcha()
          } else if (statusCode === 429) {
            errorToast(message || 'Too many attempts. Please wait and try again later.');
            setHasErrors(undefined)
            setIsRateLimited(true)
            resetCaptcha()
          } else {
            errorToast("An error occurred. Please try again later.");
            setHasErrors(true)
            setIsRateLimited(false)
            resetCaptcha()
          }
        })
        .finally(() => {
          setLoading(false)
          setSubmitting(false)
        })
    },
    
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Forgot Password ?</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          Enter your email to reset your password.
        </div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            Sorry, looks like email is incorrect, please try again.
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Sent password reset. Please check your email</div>
        </div>
      )}
      {/* end::Title */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      <div className='captcha-box my-2 overflow-hidden'>
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY!}
          onChange={(val) => {
            captchaTokenRef.current = val
            setCaptchaToken(val)
          }}
        />
      </div>

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-primary me-4'
          disabled={!captchaToken || loading || isRateLimited}
        >
          <span className='indicator-label'>Submit</span>
          {loading && (
            <span className='indicator-progress'>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancel
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
